import { startCase } from 'lodash-es'
import type { AgentRunDetail, AgentRunSummary } from '~/entities/document-agent'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { pluralize } from '~/shared/lib/pluralize'
import { type Actor, HIDDEN_AUDIT_EVENTS } from '../config'
import type { Agreement } from './agreement'
import { formatMethod, MATCH_SIDES } from './match'

/** A change an event made to the document, compared to the final state when that is settled. */
export type TimelineEffect = {
  label: string
  value?: string
  codes?: string[]
  agreement?: Agreement
}

export type TimelineEvent = {
  kind: 'event'
  id: string
  at: number
  actor: Actor
  icon: string
  title: string
  detail?: string | null
  effect?: TimelineEffect | null
  notes?: string | null
  user?: string | null
}

export type TimelineSession = {
  kind: 'session'
  id: string
  at: number | null
  session: 'classification' | 'matching'
}

export type TimelineOtherRun = { kind: 'other-run'; id: string; at: number; run: AgentRunSummary }

export type TimelineEntry = TimelineEvent | TimelineSession | TimelineOtherRun

type AuditEvent = apiClient.AuditEvent

export type TimelineContext = {
  categoryLabel: (category: string) => string
  finalCategory: string | null
  finalCodes: string[] | null
  reviewed: boolean
}

export function toMs(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? null : ms
}

function userName(user: AuditEvent['user']): string | null {
  if (!user) return null
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.sub
}

/** Methods a lookup records when no rule picked a row (`match_truth.NO_MATCH_METHODS`). */
const NO_MATCH_METHODS = new Set(['none', 'auto_skipped_other'])

export function codesOf(event: AuditEvent): string[] {
  const content = event.data_content
  if (!content || !('fields' in content)) return []
  const codes = content.fields.flatMap((field) =>
    Array.isArray(field.value)
      ? field.value
          .map((value) =>
            value && typeof value === 'object' && 'code' in value ? value.code : null,
          )
          .filter((code): code is string => typeof code === 'string')
      : [],
  )
  return [...new Set(codes)]
}

function sameCodes(codes: string[], finalCodes: string[]): boolean {
  const a = new Set(codes)
  const b = new Set(finalCodes)
  return a.size === b.size && [...a].every((code) => b.has(code))
}

function codesAgreement(codes: string[], context: TimelineContext): Agreement | undefined {
  if (!context.finalCodes) return undefined
  if (sameCodes(codes, context.finalCodes)) return 'agree'
  return codes.some((code) => context.finalCodes!.includes(code)) ? 'partial' : 'differ'
}

function categoryAgreement(category: string, context: TimelineContext): Agreement | undefined {
  if (!context.reviewed || !context.finalCategory) return undefined
  return category === context.finalCategory ? 'agree' : 'differ'
}

function sideLabel(eventType: string): string {
  return eventType.startsWith('pa-') ? 'prior authorization' : 'claim'
}

/** One audit event in words, or null for events the timeline leaves out. */
function describeEvent(
  event: AuditEvent,
  context: TimelineContext,
): Omit<TimelineEvent, 'kind' | 'id' | 'at' | 'user' | 'notes'> | null {
  const type = event.event_type
  const user = userName(event.user)
  const isSystem = !user

  switch (type) {
    case 'cluster': {
      const persons = event.subject_person_count
      return {
        actor: 'production',
        icon: 'call_split',
        title: 'Split out of its consignment',
        detail:
          typeof persons === 'number'
            ? `${persons} ${pluralize(persons, 'patient')} detected in the document`
            : null,
      }
    }
    case 'class': {
      const category = event.category
      return {
        actor: 'production',
        icon: 'category',
        title: 'Production classified the document',
        detail:
          typeof event.confidence_score === 'number'
            ? `Confidence ${Math.round(event.confidence_score * 100)}%`
            : null,
        effect: category
          ? {
              label: 'Category',
              value: context.categoryLabel(category),
              agreement: categoryAgreement(category, context),
            }
          : null,
      }
    }
    case 'clsrev': {
      const category = event.category
      if (!category) {
        return event.notes
          ? { actor: 'reviewer', icon: 'sticky_note_2', title: 'A reviewer left a note' }
          : null
      }
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'category',
        title: isSystem ? 'The category was set' : 'A reviewer set the category',
        effect: { label: 'Category', value: context.categoryLabel(category) },
      }
    }
    case 'datallm':
      return {
        actor: 'production',
        icon: 'data_object',
        title: 'Production extracted the denial details',
      }
    case 'datarev':
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'data_object',
        title: isSystem
          ? 'The denial details were confirmed'
          : 'A reviewer checked the denial details',
      }
    case 'tagsllm': {
      const codes = codesOf(event)
      return {
        actor: 'production',
        icon: 'sell',
        title: 'Production tagged the document',
        effect: { label: 'Codes', codes, agreement: codesAgreement(codes, context) },
      }
    }
    case 'tagsrev': {
      const codes = codesOf(event)
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'sell',
        title: isSystem ? 'The codes were confirmed' : 'A reviewer set the codes',
        effect: { label: 'Codes', codes },
      }
    }
    case 'patientlookup':
    case 'pa-patientlookup': {
      const rows = event.patient_case_candidates?.patient_data?.length ?? 0
      // Lookup items keep their pick in `system_selected_index`, which the audit trail does not
      // serve, so whether a rule fired is read from the method alone.
      const method = event.system_match_method
      const fired = !!method && !NO_MATCH_METHODS.has(method)
      return {
        actor: 'production',
        icon: 'person_search',
        title: `Production looked up ${sideLabel(type)} cases`,
        detail: [
          `${rows} ${pluralize(rows, 'candidate')} returned`,
          fired
            ? `the ${formatMethod(method)} rule picked one`
            : rows
              ? 'no rule picked a candidate'
              : null,
        ]
          .filter(Boolean)
          .join(' · '),
      }
    }
    case 'patientrev':
    case 'pa-patientrev': {
      const noMatch = event.status === 'no_match'
      const index = typeof event.index === 'number' && event.index >= 0 ? event.index + 1 : null
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'how_to_reg',
        title: isSystem
          ? `The ${sideLabel(type)} case was confirmed`
          : `A reviewer settled the ${sideLabel(type)} case`,
        effect: {
          label: 'Case',
          value: noMatch
            ? 'No match'
            : index
              ? `Candidate ${index}`
              : startCase(event.status ?? 'validated'),
        },
      }
    }
    case 'patient-edit':
    case 'pa-patient-edit':
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'edit',
        title: `The ${sideLabel(type)} case record was edited`,
      }
    case 'reviewstatus': {
      const status = event.review_status
      if (!status) return null
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: status === 'reviewed' || status === 'auto_reviewed' ? 'verified' : 'flag',
        title: `Status changed to ${startCase(status)}`,
        detail: event.prev_review_status ? `Was ${startCase(event.prev_review_status)}` : null,
      }
    }
    case 'payors':
      return {
        actor: 'production',
        icon: 'account_balance',
        title: 'Production matched the payor',
        detail: event.payor_match?.payor?.payorName ?? null,
      }
    case 'payorchange':
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'account_balance',
        title: 'The payor was changed',
      }
    case 'onshorechange':
      return {
        actor: isSystem ? 'production' : 'reviewer',
        icon: 'public',
        title: event.new_onshore_only ? 'Marked onshore-only' : 'Onshore-only removed',
      }
    default:
      return type.startsWith('eob_')
        ? {
            actor: isSystem ? 'production' : 'reviewer',
            icon: 'receipt_long',
            title: startCase(type),
          }
        : null
  }
}

/** When the matching session started: its first lookup, if it made one. */
function matchingStart(run: AgentRunDetail): number | null {
  const times = MATCH_SIDES.flatMap((side) =>
    (run.match?.[side]?.calls ?? []).map((call) => toMs(call.lookup?.at ?? null)),
  ).filter((value): value is number => value !== null)
  return times.length ? Math.min(...times) : null
}

/**
 * The document's history and the agent's sessions on one line, oldest first. A session with no
 * recorded start goes last.
 */
export function buildTimeline({
  events,
  run,
  otherRuns,
  hasClassification,
  hasMatching,
  context,
}: {
  events: AuditEvent[]
  run: AgentRunDetail
  otherRuns: AgentRunSummary[]
  hasClassification: boolean
  hasMatching: boolean
  context: TimelineContext
}): TimelineEntry[] {
  const entries: TimelineEntry[] = []

  for (const event of events) {
    if (HIDDEN_AUDIT_EVENTS.has(event.event_type)) continue
    const description = describeEvent(event, context)
    if (!description) continue
    entries.push({
      kind: 'event',
      id: event.key,
      at: toMs(event.timestamp) ?? 0,
      user: userName(event.user),
      notes: event.notes?.replace(/^\[Mirror\] /, '') || null,
      ...description,
    })
  }

  const runStart = toMs(run.created)
  if (hasClassification) {
    entries.push({ kind: 'session', id: 'classification', at: runStart, session: 'classification' })
  }
  if (hasMatching) {
    entries.push({
      kind: 'session',
      id: 'matching',
      at: matchingStart(run) ?? runStart,
      session: 'matching',
    })
  }
  for (const other of otherRuns) {
    const at = toMs(other.created)
    if (at !== null) entries.push({ kind: 'other-run', id: other.run_id, at, run: other })
  }

  // A stable sort keeps the matching session after classification when they share a time.
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (a.entry.at ?? Infinity) - (b.entry.at ?? Infinity) || a.index - b.index)
    .map(({ entry }) => entry)
}
